/* eslint-disable complexity */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

import {
    ApiHandlerOptions,
    ApiResponse,
    ErrorHandlingStrategy,
    MessageLevel,
    ToastMessage
} from '@/types/api'
import { isExludedPath } from '@/utils/excludedPaths'

export class ApiHandler {
    private api: AxiosInstance
    private options: ApiHandlerOptions
    private isRefreshing = false
    private refreshSubscribers: Array<(token: string) => void> = []
    private authFailureCount = 0
    private static MAX_AUTH_FAILURES = 3

    constructor(options: ApiHandlerOptions) {
        this.options = options
        this.api = this.createAxiosInstance()
        this.setupInterceptors()
    }

    private createAxiosInstance(): AxiosInstance {
        return axios.create({
            baseURL: this.options.baseURL,
            timeout: this.options.timeout || 10000,
            headers: {
                'Content-Type': 'application/json'
            }
        })
    }

    private setupInterceptors(): void {
        this.setupRequestInterceptor()
        this.setupResponseInterceptor()
    }

    private setupRequestInterceptor(): void {
        this.api.interceptors.request.use(
            (config) => {
                // Build the full URL to check against excluded paths
                const fullUrl = `${config.baseURL || ''}${config.url || ''}`
                const shouldSkipAuth = config.skipAuth || isExludedPath(fullUrl)
                const optionalAuth = config.optionalAuth === true

                console.log('🔍 DEBUG - Checking URL:', fullUrl)
                console.log('🔍 DEBUG - Should skip auth:', shouldSkipAuth)
                console.log('🔍 DEBUG - Base URL:', config.baseURL)
                console.log('🔍 DEBUG - URL:', config.url)

                if (shouldSkipAuth) {
                    console.log('Skipping authentication for path:', fullUrl)
                    // Ensure we remove any auth headers
                    if (config.headers) {
                        delete config.headers.Authorization
                        delete config.headers.common?.Authorization
                        delete config.headers.Origin
                        delete config.headers.Referer
                    }
                    // Mark this request to skip auth error handling
                    config.skipAuthErrorHandling = true

                    return config
                }

                // Only get token for non-skipped auth routes
                const token = this.options.getAccessToken()

                console.log(
                    'Using token for request:',
                    token?.substring(0, 20) + '...'
                )

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                } else if (optionalAuth) {
                    // Send request without token; do not trigger onAuthError (e.g. resend confirmation, reset password)
                    config.skipAuthErrorHandling = true
                } else {
                    console.warn('No token available for request')
                    this.options.onAuthError()
                }

                return config
            },
            (error) => Promise.reject(error)
        )
    }

    private getLatestAccessToken(): string | null {
        // First try the options getter which should use the appropriate storage based on user type
        const storeToken = this.options.getAccessToken()

        if (storeToken) {
            return storeToken
        }

        // If no token was found through the getter, check both storage locations directly
        // This is a fallback mechanism only
        try {
            // Check session storage (for agents)
            const sessionToken = sessionStorage.getItem('accessToken')

            if (sessionToken) {
                console.warn(
                    'Token found in sessionStorage but not through getter'
                )

                return sessionToken
            }

            // Check local storage (for regular users)
            const localToken = localStorage.getItem('accessToken')

            if (localToken) {
                console.warn(
                    'Token found in localStorage but not through getter'
                )

                return localToken
            }
        } catch (error) {
            console.error('Error accessing storage:', error)
        }

        // No token found anywhere
        return null
    }

    public refreshApiInstance(): void {
        // Force cleanup of any pending requests
        if (this.api) {
            try {
                // Cancel all pending requests
                if (this.api.interceptors) {
                    // Remove all interceptors
                    this.api.interceptors.request.eject(0)
                    this.api.interceptors.response.eject(0)
                }
            } catch (err) {
                console.error('Error cleaning up API instance:', err)
            }
        }

        // Create a fresh instance to ensure it picks up the latest tokens
        this.api = this.createAxiosInstance()
        this.setupInterceptors()
        console.log('API instance has been completely refreshed')
    }

    private refreshToken(
        error: any,
        originalRequest: any,
        errorStatus: number
    ): Promise<any> {
        return new Promise(async (resolve, reject) => {
            try {
                // Get the current token that just failed
                const oldAccessToken = this.options.getAccessToken()
                const refreshToken = this.options.getRefreshToken()

                if (!oldAccessToken || !refreshToken) {
                    // If tokens are missing, force logout
                    console.error('Missing tokens for refresh, logging out')
                    this.isRefreshing = false
                    this.refreshSubscribers = []
                    this.options.onAuthError()
                    reject(error)

                    return
                }

                // Try to get a new access token using the refresh token
                const newAccessToken = await this.options.getNewAccessToken(
                    oldAccessToken,
                    refreshToken
                )

                if (!newAccessToken) {
                    console.error('Failed to get a new access token')
                    // Notify all subscribers about refresh failure
                    this.refreshSubscribers.forEach((callback) => callback(''))
                    // Force logout on token refresh failure
                    this.isRefreshing = false
                    this.refreshSubscribers = []
                    this.options.onAuthError()
                    reject(error)

                    return
                }

                console.log(
                    'newAccessToken received:',
                    newAccessToken.substring(0, 20) + '...'
                )

                // CRITICAL: Wait for token to be saved
                await new Promise((waitResolve) =>
                    setTimeout(waitResolve, errorStatus === 401 ? 1000 : 1500)
                )

                // Create a completely fresh Axios instance to avoid any token caching
                this.api = this.createAxiosInstance()
                this.setupInterceptors()
                console.log('API instance has been completely rebuilt')

                // Notify all subscribers about the new token
                this.refreshSubscribers.forEach((callback) =>
                    callback(newAccessToken)
                )
                this.refreshSubscribers = []

                // Create a brand new request object to avoid any caching
                const newRequest = { ...originalRequest }

                // Set the Authorization header with the new token
                newRequest.headers = {
                    ...originalRequest.headers,
                    'Cache-Control': 'no-cache, no-store',
                    Pragma: 'no-cache',
                    Authorization: `Bearer ${newAccessToken}`
                }

                console.log(
                    'Retrying request with fresh config:',
                    newRequest.url
                )

                // Use our rebuilt API instance
                resolve(this.api(newRequest))
            } catch (refreshError) {
                console.error('Error during token refresh:', refreshError)
                // Force logout on token refresh error
                this.isRefreshing = false
                this.refreshSubscribers = []
                this.options.onAuthError()
                reject(refreshError)
            }
        })
    }

    private setupResponseInterceptor(): void {
        this.api.interceptors.response.use(
            (response) => {
                this.handleResponse(response.data)

                return response
            },
            async (error) => {
                const originalRequest = error.config
                const errorStatus = error.response?.status

                // Don't handle auth errors for routes that skip auth
                if (originalRequest?.skipAuthErrorHandling) {
                    return Promise.reject(error)
                }

                // Track consecutive auth failures to prevent infinite loops
                if (errorStatus === 401 || errorStatus === 403) {
                    this.authFailureCount++
                    console.warn(`Auth failure count: ${this.authFailureCount}`)

                    // Force logout if we've had too many consecutive auth failures
                    if (this.authFailureCount >= ApiHandler.MAX_AUTH_FAILURES) {
                        console.error(
                            `Maximum auth failures (${ApiHandler.MAX_AUTH_FAILURES}) reached, forcing logout`
                        )
                        this.authFailureCount = 0
                        this.options.onAuthError()

                        return Promise.reject(
                            new Error('Too many authentication failures')
                        )
                    }
                }

                if (
                    (errorStatus === 401 || errorStatus === 403) &&
                    !originalRequest._retry
                ) {
                    if (this.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            this.refreshSubscribers.push((token) => {
                                if (token) {
                                    originalRequest.headers.Authorization = `Bearer ${token}`
                                    resolve(this.api(originalRequest))
                                } else {
                                    // If no token received, reject the promise
                                    reject(new Error('Token refresh failed'))
                                }
                            })
                        })
                    }

                    originalRequest._retry = true
                    this.isRefreshing = true

                    try {
                        return await this.refreshToken(
                            error,
                            originalRequest,
                            errorStatus
                        )
                    } finally {
                        this.isRefreshing = false
                    }
                } else if (errorStatus === 401 || errorStatus === 403) {
                    const isCorsError =
                        error.response?.data === 'Invalid CORS request'
                    const isExcludedPath =
                        error.config?.url && isExludedPath(error.config.url)
                    const isLoginPage = window.location.pathname === '/login'

                    if (
                        errorStatus === 403 &&
                        (isCorsError || isExcludedPath)
                    ) {
                        const config = { ...error.config }

                        if (config.headers) {
                            delete config.headers.Authorization
                        }

                        return this.api.request(config)
                    }

                    // Solo suprimir toasts si NO estamos en la página de login
                    if (!isLoginPage) {
                        try {
                            const href =
                                window.location.pathname +
                                window.location.search +
                                window.location.hash

                            localStorage.setItem('postLoginRedirect', href)
                            localStorage.setItem('suppressErrorToasts', 'true')
                        } catch {}
                        // Solo llamar onAuthError si NO estamos en la página de login
                        this.options.onAuthError()
                    }
                }

                // Network/CORS errors (no response reaches the browser)
                if (
                    !error.response &&
                    !originalRequest?.skipAuthErrorHandling
                ) {
                    const isLoginPage = window.location.pathname === '/login'

                    if (!isLoginPage) {
                        try {
                            const href =
                                window.location.pathname +
                                window.location.search +
                                window.location.hash

                            localStorage.setItem('postLoginRedirect', href)
                            localStorage.setItem('suppressErrorToasts', 'true')
                        } catch {}
                        this.options.onAuthError()
                    }
                }

                return Promise.reject(error)
            }
        )
    }

    private handleResponse<T>(response: ApiResponse<T>): void {
        try {
            const suppress =
                localStorage.getItem('suppressErrorToasts') === 'true'

            if (suppress) {
                return
            }
        } catch {}

        if (!response.message?.length) {
            return
        }

        const { messageLevel } = this.options

        response.message.forEach((msg) => {
            const type = msg.messageType.toLowerCase() as ToastMessage['type']

            if (
                messageLevel === MessageLevel.NONE ||
                (messageLevel === MessageLevel.ERRORS_AND_INFO_ONLY &&
                    type !== 'error' &&
                    type !== 'info') ||
                (messageLevel === MessageLevel.SUCCESS_ONLY &&
                    type !== 'success') ||
                (messageLevel === MessageLevel.ERRORS && type !== 'error')
            ) {
                return
            }

            // No mostrar toast de "no se encontró información relacionada" en carga inicial
            const desc = (msg.messageDesc || '').toLowerCase()
            if (
                desc.includes('información relacionada') ||
                desc.includes('informacion relacionada')
            ) {
                return
            }

            this.options.showMessage({
                message: msg.messageDesc,
                type
            })
        })
    }

    private async handleRequest<T>(
        requestFn: () => Promise<ApiResponse<T>>
    ): Promise<ApiResponse<T>> {
        try {
            const response = await requestFn()

            this.handleResponse(response)

            return response
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const apiError = (error.response?.data as ApiResponse<T>) ?? {
                    data: undefined as unknown as T,
                    status: error.response?.status ?? 0,
                    message: [
                        {
                            messageDesc: 'Network/CORS error',
                            messageType: 'ERROR'
                        }
                    ],
                    messageType: 'ERROR',
                    dataType: 'EMPTY'
                }

                this.handleResponse(apiError)

                if (
                    this.options.errorHandling === ErrorHandlingStrategy.THROW
                ) {
                    throw error
                }

                return apiError
            }
            throw error
        }
    }

    async get<T>(
        url: string,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        return this.handleRequest(async () => {
            const response = await this.api.get(url, config)

            return response.data
        })
    }

    async post<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        return this.handleRequest(async () => {
            const response = await this.api.post(url, data, config)

            return response.data
        })
    }

    async put<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        return this.handleRequest(async () => {
            const response = await this.api.put(url, data, config)

            return response.data
        })
    }

    async patch<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        return this.handleRequest(async () => {
            const response = await this.api.patch(url, data, config)

            return response.data
        })
    }

    async delete<T>(
        url: string,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        return this.handleRequest(async () => {
            const response = await this.api.delete(url, config)

            return response.data
        })
    }
}
